import React, { useMemo } from 'react';
import { CellInfo } from 'react-table';
import { ArchivesTableCell } from '../ArchivesStyles';
import { Table, TableHead, TableBody, TableBodyCell, tableHooks, TableContainer, Loader } from '@trackman/web-shared-components';
import { StyledTableBodyRow, StyledTableHeadCell, StyledTableHeadRow, TableBodyContent } from './BpTableStyles';

interface IProps {
  data: any[];
  loading: boolean;
}

const BpTable: React.FC<IProps> = (props) => {
  const { data, loading } = props;

  const renderCell = (info: CellInfo): JSX.Element => (
    <ArchivesTableCell title={info.value as string} data-test={`${info.column.id}-${info.row.id}`}>
      {info.value as string}
    </ArchivesTableCell>
  );

  const { useTable, useSortBy, usePagination, useRowSelect, useResizeColumns, useFlexLayout } = tableHooks;

  const columns = useMemo(
    () => [
      {
        Header: 'Start time',
        accessor: 'startTime',
        Cell: renderCell,
      },
      {
        Header: 'Away/Visiting Team',
        accessor: 'awayTeam',
        Cell: renderCell,
      },
      { Header: 'Home Team', accessor: 'homeTeam', Cell: renderCell },
      {
        Header: 'League',
        accessor: 'league',
        Cell: renderCell,
        width: 110,
      },
      {
        Header: 'Level',
        accessor: 'level',
        Cell: renderCell,
        width: 110,
      },
      {
        Header: 'Field',
        accessor: 'fieldName',
        Cell: renderCell,
        width: 130,
      },
      { Header: 'Status', accessor: 'reviewButton', width: 100 },
    ],
    [data]
  );

  const { getTableProps, rows, headerGroups, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useFlexLayout,
    useResizeColumns
  );

  return (
    <TableContainer>
      <Table {...getTableProps()}>
        <TableHead dark>
          {headerGroups.map(({ headers, getHeaderGroupProps }) => (
            <StyledTableHeadRow {...getHeaderGroupProps()}>
              {headers.map(({ sortable, isSorted, isSortedDesc, align, render, getHeaderProps, getSortByToggleProps }) => (
                <StyledTableHeadCell
                  {...getHeaderProps(sortable && getSortByToggleProps())}
                  sortable={sortable}
                  sortDirection={isSorted ? (isSortedDesc ? 'desc' : 'asc') : false}
                  align={align}
                >
                  {render('Header')}
                </StyledTableHeadCell>
              ))}
            </StyledTableHeadRow>
          ))}
        </TableHead>
        <TableBody>
          <TableBodyContent>
            {loading ? (
              <div className='loader'>
                <Loader />
              </div>
            ) : (
              rows.map((row, index) => {
                prepareRow(row);
                return (
                  <StyledTableBodyRow index={index} {...row.getRowProps()}>
                    {row.cells.map(({ getCellProps, column, render }) => (
                      <TableBodyCell {...getCellProps()} align={column.align}>
                        {render('Cell')}
                      </TableBodyCell>
                    ))}
                  </StyledTableBodyRow>
                );
              })
            )}
          </TableBodyContent>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BpTable;
